import { AutoStack, FlexSpinner } from 'components'
import { DownloadLink } from 'components/DownloadLink'
import { FormikTagPicker } from 'components/TagPicker/FormikTagPicker'
import { FormikDropdown, FormikTextField } from 'formik-office-ui-fabric-react'
import { FieldStack, FieldStackItem, FormStack } from 'forms/FormStack'
import { getDropdownOptionsForEnum } from 'helpers'
import {
  FileTemplate,
  FileTemplateType,
  useProvingGroundDropdownQuery,
} from 'model'
import {
  DefaultButton,
  Dropdown,
  IDropdownOption,
  ITag,
  Label,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { isUndefined } from 'util'
import { useGenerateFileFromTemplateMutation } from 'views/AdminFileTemplatesView/AdminFileTemplatesView.generated'
import * as Yup from 'yup'
import { useProvingIdsQuery } from './FileTemplateForm.generated'

export interface FileTemplateFormValues {
  provingGround?: string
  cellMapping?: string
  fileName: string
  type: FileTemplateType
}

export class FileTemplateFormSchema {
  constructor(fileTemplates: FileTemplate[]) {
    Yup.object().shape({
      uri: Yup.string().trim(),
      type: Yup.string().trim(),
      cellMapping: Yup.string().trim(),
      provingGround: Yup.string().trim(),
    })
  }
}

export const FileTemplateForm: React.FC = () => {
  const { t } = useTranslation()
  const [testUri, setTestUri] = React.useState<string>()
  const [fileUri, setFileUri] = React.useState<string>()
  const [mapping, setMapping] = React.useState<string>('')
  const [testProvingId, setTestProvingId] = React.useState<string>('')
  const [errorMsg, setErrorMsg] = React.useState<string>()
  const [generateFileFromTemplate] = useGenerateFileFromTemplateMutation()
  const [testTemplateLoading, setTestTemplateLoading] = React.useState(false)
  const provingsQuery = useProvingIdsQuery()
  const pgDropdownQuery = useProvingGroundDropdownQuery()
  const { loading, error } = pgDropdownQuery
  const provingGrounds =
    !pgDropdownQuery.loading &&
    pgDropdownQuery.data &&
    pgDropdownQuery.data.provingGrounds
      ? pgDropdownQuery.data.provingGrounds
      : []

  const provingIds =
    !provingsQuery.loading && provingsQuery.data && provingsQuery.data.provings
      ? provingsQuery.data.provings.map((p) => p.id)
      : []

  const provingGroundOptions: ITag[] = [
    { key: '', name: t('model.fileTemplate.allProvingGrounds') },
    ...provingGrounds.map(
      (pg) =>
        ({
          key: pg._id,
          name: pg.title,
        } as ITag)
    ),
  ]
  const provingIdOptions: IDropdownOption[] = [
    { key: '', text: t('Erprobung zum Test auswählen') },
    ...provingIds.map((pId) => ({ key: pId, text: pId })),
  ]
  const fileTemplateTypeOptions: IDropdownOption[] =
    getDropdownOptionsForEnum<FileTemplate>(
      t,
      FileTemplateType,
      'fileTemplateType'
    )

  const handleTestButtonClick = async () => {
    setTestTemplateLoading(true)
    let testTemplateUri = ''

    try {
      if (mapping && fileUri && testProvingId) {
        const testData = await generateFileFromTemplate({
          variables: {
            templateUri: fileUri,
            mapping,
            provingId: testProvingId,
            isTestDoc: true,
          },
        })

        if (testData.data && testData.data.generateFileFromTemplate) {
          testTemplateUri = testData.data.generateFileFromTemplate.uri
        }
      }
    } catch (err) {
      setErrorMsg(err)
    }

    setTestUri(testTemplateUri)
    setTestTemplateLoading(false)
  }

  return (
    <FormStack>
      <FieldStack>
        <FlexSpinner loading={loading} error={error}>
          <FieldStackItem
            name="provingGround"
            render={(fieldProps) => (
              <>
                <Label required={true}>{t('field.provingGround.label')}</Label>
                <FormikTagPicker {...fieldProps} tags={provingGroundOptions} />
              </>
            )}
          />
        </FlexSpinner>
        <FieldStackItem
          name="type"
          render={(fieldProps) => (
            <>
              <Label required={true}>{t('field.fileTemplateType.label')}</Label>
              <FormikDropdown
                {...fieldProps}
                options={fileTemplateTypeOptions}
              />
            </>
          )}
        />
        <FieldStackItem
          name="cellMapping"
          render={(fieldProps) => {
            setMapping(fieldProps.field.value)
            setFileUri(fieldProps.form.values.uri)
            return (
              <>
                <Label>{t('field.cellMapping.label')}</Label>
                <FormikTextField {...fieldProps} multiline={true} />
              </>
            )
          }}
          component={FormikTextField}
        />
      </FieldStack>
      <AutoStack horizontal={true}>
        <Dropdown
          style={{ width: 300 }}
          defaultSelectedKey={testProvingId}
          options={provingIdOptions}
          onChange={(_, item) =>
            setTestProvingId(item ? (item.key as string) : '')
          }
        />
        <DefaultButton
          text={t('model.fileTemplate.test')}
          onClick={handleTestButtonClick}
          disabled={
            testProvingId === '' || isUndefined(testProvingId) || mapping === ''
          }
        />
      </AutoStack>
      <FlexSpinner loading={testTemplateLoading} />
      {testUri && !testTemplateLoading && (
        <DownloadLink uri={testUri}>
          {t('model.fileTemplate.downloadTestfile')}
        </DownloadLink>
      )}
      {errorMsg && !testUri && (
        <pre style={{ color: 'red' }}>{JSON.stringify(errorMsg, null, 2)}</pre>
      )}
    </FormStack>
  )
}
